import { createFileRoute } from "@tanstack/react-router";
import { useRef, useState } from "react";
import { toast } from "sonner";
import {
  User, Bell, Shield, CreditCard, Crown, Upload, Save, Check, Smartphone,
  Key, Mail, Building2, Wallet, Plus, Trash2, ArrowUpRight, Calendar,
  AlertCircle, Copy,
} from "lucide-react";

import { MerchantShell } from "@/components/merchant/merchant-shell";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import { Riyal } from "@/components/ui/riyal";

export const Route = createFileRoute("/merchant/settings")({
  head: () => ({
    meta: [
      { title: "Settings — Spayzo Merchant" },
      { name: "description", content: "Manage your profile, notifications, security and subscription." },
    ],
  }),
  component: SettingsPage,
});

const TABS = [
  { id: "profile", icon: User },
  { id: "notifications", icon: Bell },
  { id: "security", icon: Shield },
  { id: "payment", icon: CreditCard },
  { id: "subscription", icon: Crown },
] as const;

function SectionCard({ title, description, children, action }: {
  title: string; description?: string; children: React.ReactNode; action?: React.ReactNode;
}) {
  return (
    <Card className="rounded-2xl border-border/60 p-6 shadow-soft">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="font-display text-base font-semibold">{title}</h3>
          {description && <p className="mt-0.5 text-xs text-muted-foreground">{description}</p>}
        </div>
        {action}
      </div>
      <div className="mt-5">{children}</div>
    </Card>
  );
}

function Row({ icon: Icon, title, description, children }: {
  icon?: React.ComponentType<{ className?: string }>; title: string;
  description?: string; children: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between gap-4 py-3.5">
      <div className="flex min-w-0 items-start gap-3">
        {Icon && (
          <span className="mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-primary/10 text-primary">
            <Icon className="h-4 w-4" />
          </span>
        )}
        <div className="min-w-0">
          <p className="text-sm font-semibold">{title}</p>
          {description && <p className="mt-0.5 text-xs text-muted-foreground">{description}</p>}
        </div>
      </div>
      <div className="shrink-0">{children}</div>
    </div>
  );
}

type Session = { id: string; deviceKey: string; locKey: string; current?: boolean };
type Wallet = { id: string; kind: "PayPal" | "Apple Pay" | "Google Pay"; label: string };
type CardItem = { id: string; brand: string; last4: string; exp: string; isDefault?: boolean };
type Plan = "Basic" | "Pro" | "Premium";

const PLAN_PRICE: Record<Plan, number> = { Basic: 19, Pro: 49, Premium: 129 };

function SettingsPage() {
  const { t } = useTranslation();
  const s = (k: string, o?: Record<string, unknown>) =>
    t(`merchantSettings.${k}`, o as never) as unknown as string;
  const [tab, setTab] = useState<(typeof TABS)[number]["id"]>("profile");
  const save = (msg = s("toast.saved")) =>
    toast.success(msg, { description: s("toast.savedDesc") });

  // Profile
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const onLogo = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error(s("toast.logoTooLarge"));
      return;
    }
    const url = URL.createObjectURL(file);
    setLogoUrl(url);
    toast.success(s("toast.logoUploaded"));
  };

  // Security
  const [sessions, setSessions] = useState<Session[]>([
    { id: "1", deviceKey: "security.devices.mac", locKey: "security.devices.macLoc", current: true },
    { id: "2", deviceKey: "security.devices.iphone", locKey: "security.devices.iphoneLoc" },
    { id: "3", deviceKey: "security.devices.chrome", locKey: "security.devices.chromeLoc" },
  ]);
  const [revokeId, setRevokeId] = useState<string | null>(null);
  const [codesOpen, setCodesOpen] = useState(false);
  const recoveryCodes = [
    "9K2X-4L7Q", "H3P8-M2VD", "R6WN-K1YZ", "T4BC-9SPE", "X8LM-7QRA",
    "V2ND-K4JX", "P9FT-2LBM", "M7CX-Q3RN", "H1KP-8VWT", "Y6JR-4NPL",
  ];
  const copyCodes = async () => {
    try {
      await navigator.clipboard.writeText(recoveryCodes.join("\n"));
      toast.success(s("toast.codesCopied"));
    } catch {
      toast.error(s("toast.copyFailed"));
    }
  };

  // Payment
  const [wallets, setWallets] = useState<Wallet[]>([
    { id: "w1", kind: "PayPal", label: "amelia@centralcoffee.de" },
  ]);
  const [walletOpen, setWalletOpen] = useState(false);
  const [walletForm, setWalletForm] = useState<{ kind: Wallet["kind"]; label: string }>({
    kind: "PayPal", label: "",
  });
  const addWallet = () => {
    if (!walletForm.label.trim()) return toast.error(s("toast.walletIdRequired"));
    setWallets((w) => [...w, { id: `w-${Date.now()}`, ...walletForm, label: walletForm.label.trim() }]);
    setWalletOpen(false);
    setWalletForm({ kind: "PayPal", label: "" });
    toast.success(s("toast.walletAdded"));
  };
  const removeWallet = (id: string) => {
    setWallets((w) => w.filter((x) => x.id !== id));
    toast.success(s("toast.walletRemoved"));
  };

  const [cards, setCards] = useState<CardItem[]>([
    { id: "c1", brand: "VISA", last4: "4242", exp: "09/28", isDefault: true },
  ]);
  const [cardOpen, setCardOpen] = useState(false);
  const [cardForm, setCardForm] = useState({ number: "", exp: "", cvc: "" });
  const addCard = () => {
    const digits = cardForm.number.replace(/\D/g, "");
    if (digits.length < 12) return toast.error(s("toast.invalidCard"));
    if (!/^\d{2}\/\d{2}$/.test(cardForm.exp)) return toast.error(s("toast.invalidExpiry"));
    if (cardForm.cvc.length < 3) return toast.error(s("toast.invalidCvc"));
    setCards((c) => [
      ...c,
      { id: `card-${Date.now()}`, brand: "VISA", last4: digits.slice(-4), exp: cardForm.exp },
    ]);
    setCardOpen(false);
    setCardForm({ number: "", exp: "", cvc: "" });
    toast.success(s("toast.cardAdded"));
  };
  const removeCard = (id: string) => {
    setCards((c) => c.filter((x) => x.id !== id));
    toast.success(s("toast.cardRemoved"));
  };
  const makeDefault = (id: string) => {
    setCards((c) => c.map((x) => ({ ...x, isDefault: x.id === id })));
    toast.success(s("toast.defaultCardUpdated"));
  };

  // Subscription
  const [currentPlan, setCurrentPlan] = useState<Plan>("Pro");
  const [switchTo, setSwitchTo] = useState<Plan | null>(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [autoRenew, setAutoRenew] = useState(true);
  const planName = (p: Plan) => s(`subscription.plans.${p}`);
  const confirmSwitch = () => {
    if (!switchTo) return;
    setCurrentPlan(switchTo);
    toast.success(s("toast.planUpdated", { plan: planName(switchTo) }));
    setSwitchTo(null);
  };

  const currentFeatures = [
    "spaces15", "smartOffersUpsell", "advancedAnalytics", "prioritySupport", "customBranding",
  ];
  const planCards: { name: Plan; recommended?: boolean; features: string[] }[] = [
    { name: "Basic", features: ["spaces3", "basicAnalytics", "emailSupport"] },
    { name: "Pro", features: ["spaces15", "smartOffers", "advancedAnalytics"] },
    { name: "Premium", recommended: true, features: ["spacesUnlimited", "multiLocation", "dedicatedManager"] },
  ];

  return (
    <MerchantShell
      title={s("title")}
      subtitle={s("subtitle")}
    >
      <Tabs value={tab} onValueChange={(v) => setTab(v as typeof tab)}>
        <div className="flex flex-col gap-6 lg:flex-row">
          <aside className="lg:w-60 lg:shrink-0">
            <TabsList className="h-auto w-full justify-start gap-1 overflow-x-auto rounded-2xl bg-background p-1.5 shadow-soft ring-1 ring-border/60 lg:flex-col lg:overflow-visible lg:bg-background">
              {TABS.map((item) => (
                <TabsTrigger
                  key={item.id} value={item.id}
                  className="w-full justify-start gap-2.5 rounded-xl px-3 py-2.5 text-sm data-[state=active]:bg-primary/10 data-[state=active]:text-primary data-[state=active]:shadow-none"
                >
                  <item.icon className="h-4 w-4" />
                  <span>{s(`tabs.${item.id}`)}</span>
                </TabsTrigger>
              ))}
            </TabsList>
          </aside>

          <div className="flex-1 space-y-6">
            {/* PROFILE */}
            <TabsContent value="profile" className="mt-0 space-y-6">
              <SectionCard
                title={s("profile.title")}
                description={s("profile.desc")}
              >
                <div className="flex flex-wrap items-center gap-5">
                  <div className="relative">
                    <div className="grid h-20 w-20 place-items-center overflow-hidden rounded-2xl bg-gradient-to-br from-primary/30 to-primary/10 text-2xl font-bold text-primary shadow-soft">
                      {logoUrl ? (
                        <img src={logoUrl} alt={s("profile.logo")} className="h-full w-full object-cover" />
                      ) : (
                        "CC"
                      )}
                    </div>
                    <button
                      type="button"
                      aria-label={s("profile.uploadLogo")}
                      onClick={() => logoInputRef.current?.click()}
                      className="absolute -bottom-1 -end-1 grid h-7 w-7 place-items-center rounded-full bg-primary text-primary-foreground shadow-soft ring-2 ring-background transition hover:scale-105"
                    >
                      <Upload className="h-3.5 w-3.5" />
                    </button>
                    <input
                      ref={logoInputRef} type="file" accept="image/png,image/jpeg"
                      className="hidden" onChange={onLogo}
                    />
                  </div>
                  <div className="min-w-[200px] flex-1">
                    <p className="text-sm font-semibold">{s("profile.logo")}</p>
                    <p className="text-xs text-muted-foreground">{s("profile.logoHint")}</p>
                    <div className="mt-2 flex gap-2">
                      <Button
                        variant="outline" size="sm" className="h-9 rounded-xl"
                        onClick={() => logoInputRef.current?.click()}
                      >
                        <Upload className="h-3.5 w-3.5" /> {s("profile.uploadNew")}
                      </Button>
                      <Button
                        variant="ghost" size="sm"
                        className="h-9 rounded-xl text-destructive hover:text-destructive"
                        onClick={() => { setLogoUrl(null); toast.success(s("toast.logoRemoved")); }}
                      >
                        {s("profile.remove")}
                      </Button>
                    </div>
                  </div>
                </div>

                <Separator className="my-6" />

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <Label htmlFor="biz" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{s("profile.businessName")}</Label>
                    <Input id="biz" defaultValue="Central Coffee Co." className="mt-1.5 h-11 rounded-xl" />
                  </div>
                  <div>
                    <Label htmlFor="legal" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{s("profile.legalEntity")}</Label>
                    <Input id="legal" defaultValue="Central Coffee GmbH" className="mt-1.5 h-11 rounded-xl" />
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{s("profile.contactEmail")}</Label>
                    <Input id="email" type="email" dir="ltr" defaultValue="amelia@centralcoffee.de" className="mt-1.5 h-11 rounded-xl" />
                  </div>
                  <div>
                    <Label htmlFor="phone" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{s("profile.phone")}</Label>
                    <Input id="phone" dir="ltr" defaultValue="+49 30 123 4567" className="mt-1.5 h-11 rounded-xl" />
                  </div>
                  <div className="sm:col-span-2">
                    <Label htmlFor="addr" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{s("profile.address")}</Label>
                    <Input id="addr" defaultValue="Torstraße 142, 10119 Berlin, Germany" className="mt-1.5 h-11 rounded-xl" />
                  </div>
                  <div className="sm:col-span-2">
                    <Label htmlFor="bio" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{s("profile.about")}</Label>
                    <Textarea id="bio" rows={3} className="mt-1.5 rounded-xl"
                      defaultValue="Premium coworking, photo and event spaces in the heart of Berlin." />
                  </div>
                </div>

                <div className="mt-6 flex flex-wrap items-center justify-end gap-2 border-t border-border/60 pt-5">
                  <Button
                    variant="ghost" className="rounded-xl"
                    onClick={() => toast.message(s("toast.discarded"))}
                  >
                    {s("profile.cancel")}
                  </Button>
                  <Button className="rounded-xl shadow-soft" onClick={() => save(s("toast.profileUpdated"))}>
                    <Save className="h-4 w-4" /> {s("profile.save")}
                  </Button>
                </div>
              </SectionCard>
            </TabsContent>

            {/* NOTIFICATIONS */}
            <TabsContent value="notifications" className="mt-0 space-y-6">
              <SectionCard title={s("notifications.emailTitle")} description={s("notifications.emailDesc")}>
                <div className="divide-y divide-border/60">
                  <Row icon={Mail} title={s("notifications.productUpdates")} description={s("notifications.productUpdatesDesc")}>
                    <Switch defaultChecked />
                  </Row>
                  <Row icon={Mail} title={s("notifications.weeklySummary")} description={s("notifications.weeklySummaryDesc")}>
                    <Switch defaultChecked />
                  </Row>
                  <Row icon={Mail} title={s("notifications.marketingTips")} description={s("notifications.marketingTipsDesc")}>
                    <Switch />
                  </Row>
                </div>
              </SectionCard>

              <SectionCard title={s("notifications.bookingTitle")} description={s("notifications.bookingDesc")}>
                <div className="divide-y divide-border/60">
                  <Row icon={Bell} title={s("notifications.newBooking")} description={s("notifications.newBookingDesc")}>
                    <Switch defaultChecked />
                  </Row>
                  <Row icon={Bell} title={s("notifications.cancellations")} description={s("notifications.cancellationsDesc")}>
                    <Switch defaultChecked />
                  </Row>
                  <Row icon={Bell} title={s("notifications.reminders")} description={s("notifications.remindersDesc")}>
                    <Switch defaultChecked />
                  </Row>
                  <Row icon={Bell} title={s("notifications.review")} description={s("notifications.reviewDesc")}>
                    <Switch />
                  </Row>
                </div>
              </SectionCard>

              <SectionCard title={s("notifications.paymentTitle")} description={s("notifications.paymentDesc")}>
                <div className="divide-y divide-border/60">
                  <Row icon={CreditCard} title={s("notifications.payoutSent")} description={s("notifications.payoutSentDesc")}>
                    <Switch defaultChecked />
                  </Row>
                  <Row icon={CreditCard} title={s("notifications.failedPayments")} description={s("notifications.failedPaymentsDesc")}>
                    <Switch defaultChecked />
                  </Row>
                  <Row icon={CreditCard} title={s("notifications.renewal")} description={s("notifications.renewalDesc")}>
                    <Switch defaultChecked />
                  </Row>
                </div>
                <div className="mt-4 flex justify-end">
                  <Button className="rounded-xl shadow-soft" onClick={() => save(s("toast.notificationsUpdated"))}>
                    <Save className="h-4 w-4" /> {s("notifications.savePrefs")}
                  </Button>
                </div>
              </SectionCard>
            </TabsContent>

            {/* SECURITY */}
            <TabsContent value="security" className="mt-0 space-y-6">
              <SectionCard title={s("security.passwordTitle")} description={s("security.passwordDesc")}>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                  <div>
                    <Label htmlFor="cur" className="text-xs text-muted-foreground">{s("security.current")}</Label>
                    <Input id="cur" type="password" className="mt-1.5 h-11 rounded-xl" placeholder="••••••••" />
                  </div>
                  <div>
                    <Label htmlFor="new" className="text-xs text-muted-foreground">{s("security.new")}</Label>
                    <Input id="new" type="password" className="mt-1.5 h-11 rounded-xl" placeholder="••••••••" />
                  </div>
                  <div>
                    <Label htmlFor="conf" className="text-xs text-muted-foreground">{s("security.confirm")}</Label>
                    <Input id="conf" type="password" className="mt-1.5 h-11 rounded-xl" placeholder="••••••••" />
                  </div>
                </div>
                <div className="mt-5 flex justify-end">
                  <Button
                    className="rounded-xl shadow-soft"
                    onClick={() => {
                      const cur = (document.getElementById("cur") as HTMLInputElement)?.value;
                      const nw = (document.getElementById("new") as HTMLInputElement)?.value;
                      const cf = (document.getElementById("conf") as HTMLInputElement)?.value;
                      if (!cur || !nw || !cf) return toast.error(s("toast.fillPasswords"));
                      if (nw.length < 12) return toast.error(s("toast.passwordTooShort"));
                      if (nw !== cf) return toast.error(s("toast.passwordMismatch"));
                      save(s("toast.passwordUpdated"));
                    }}
                  >
                    <Key className="h-4 w-4" /> {s("security.updatePassword")}
                  </Button>
                </div>
              </SectionCard>

              <SectionCard title={s("security.twoFaTitle")} description={s("security.twoFaDesc")}>
                <div className="divide-y divide-border/60">
                  <Row icon={Smartphone} title={s("security.authApp")}
                    description={s("security.authAppDesc")}>
                    <div className="flex items-center gap-2">
                      <Badge className="rounded-full bg-emerald-500/10 text-emerald-700 hover:bg-emerald-500/10">
                        <Check className="h-3 w-3" /> {s("security.active")}
                      </Badge>
                      <Button
                        variant="outline" size="sm" className="h-9 rounded-xl"
                        onClick={() => toast.message(s("toast.authSetup"))}
                      >
                        {s("security.manage")}
                      </Button>
                    </div>
                  </Row>
                  <Row icon={Mail} title={s("security.emailCodes")} description={s("security.emailCodesDesc")}>
                    <Switch
                      onCheckedChange={(v) => toast.success(v ? s("toast.emailCodesOn") : s("toast.emailCodesOff"))}
                    />
                  </Row>
                  <Row icon={Shield} title={s("security.recoveryCodes")} description={s("security.recoveryCodesDesc")}>
                    <Button
                      variant="outline" size="sm" className="h-9 rounded-xl"
                      onClick={() => setCodesOpen(true)}
                    >
                      {s("security.viewCodes")}
                    </Button>
                  </Row>
                </div>
              </SectionCard>

              <SectionCard title={s("security.sessionsTitle")} description={s("security.sessionsDesc")}>
                <div className="space-y-3">
                  {sessions.map((item) => (
                    <div key={item.id} className="flex items-center justify-between rounded-xl border border-border/60 p-3">
                      <div>
                        <p className="text-sm font-semibold">{s(item.deviceKey)}</p>
                        <p className="text-xs text-muted-foreground">{s(item.locKey)}</p>
                      </div>
                      {item.current ? (
                        <Badge className="rounded-full bg-primary/10 text-primary hover:bg-primary/10">{s("security.thisDevice")}</Badge>
                      ) : (
                        <Button
                          variant="ghost" size="sm"
                          className="text-destructive hover:text-destructive"
                          onClick={() => setRevokeId(item.id)}
                        >
                          <Trash2 className="h-3.5 w-3.5" /> {s("security.revoke")}
                        </Button>
                      )}
                    </div>
                  ))}
                  {sessions.filter((x) => !x.current).length === 0 && (
                    <p className="text-xs text-muted-foreground">{s("security.onlyThisDevice")}</p>
                  )}
                </div>
              </SectionCard>
            </TabsContent>

            {/* PAYMENT */}
            <TabsContent value="payment" className="mt-0 space-y-6">
              <SectionCard title={s("payment.bankTitle")} description={s("payment.bankDesc")}
                action={<Badge className="rounded-full bg-emerald-500/10 text-emerald-700 hover:bg-emerald-500/10"><Check className="h-3 w-3" /> {s("payment.verified")}</Badge>}>
                <div className="flex items-center gap-3 rounded-xl border border-border/60 p-3">
                  <span className="grid h-11 w-11 place-items-center rounded-xl bg-primary/10 text-primary">
                    <Building2 className="h-5 w-5" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold">{s("payment.bankName")}</p>
                    <p className="text-xs text-muted-foreground">{s("payment.bankMeta")}</p>
                  </div>
                  <Badge variant="secondary" className="rounded-full text-[10px]">{s("payment.primary")}</Badge>
                </div>
                <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <Label className="text-xs text-muted-foreground">{s("payment.accountHolder")}</Label>
                    <Input defaultValue="Central Coffee GmbH" className="mt-1.5 h-11 rounded-xl" />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">{s("payment.iban")}</Label>
                    <Input dir="ltr" defaultValue="DE89 3704 0044 0532 0130 00" className="mt-1.5 h-11 rounded-xl font-mono text-xs" />
                  </div>
                </div>
                <div className="mt-5 flex justify-end">
                  <Button className="rounded-xl shadow-soft" onClick={() => save(s("toast.bankUpdated"))}>
                    <Save className="h-4 w-4" /> {s("payment.saveBank")}
                  </Button>
                </div>
              </SectionCard>

              <SectionCard title={s("payment.walletsTitle")} description={s("payment.walletsDesc")}>
                <div className="space-y-2">
                  {wallets.map((w) => (
                    <div key={w.id} className="flex items-center gap-3 rounded-xl border border-border/60 p-3">
                      <span className="grid h-10 w-10 place-items-center rounded-xl bg-primary/10 text-primary">
                        <Wallet className="h-4 w-4" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold">{w.kind}</p>
                        <p className="truncate text-xs text-muted-foreground" dir="ltr">{w.label}</p>
                      </div>
                      <Button
                        variant="ghost" size="sm"
                        className="text-destructive hover:text-destructive"
                        onClick={() => removeWallet(w.id)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  ))}
                  {wallets.length === 0 && (
                    <p className="text-xs text-muted-foreground">{s("payment.noWallets")}</p>
                  )}
                </div>
                <Button
                  variant="outline" className="mt-3 h-10 w-full rounded-xl"
                  onClick={() => setWalletOpen(true)}
                >
                  <Plus className="h-4 w-4" /> {s("payment.addWallet")}
                </Button>
              </SectionCard>

              <SectionCard title={s("payment.cardsTitle")} description={s("payment.cardsDesc")}>
                <div className="space-y-2">
                  {cards.map((c) => (
                    <div key={c.id} className="flex items-center gap-3 rounded-xl border border-border/60 p-3">
                      <div className="grid h-10 w-14 place-items-center rounded-lg bg-foreground text-[10px] font-bold text-background">
                        {c.brand}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold" dir="ltr">•••• •••• •••• {c.last4}</p>
                        <p className="text-xs text-muted-foreground">{s("payment.expires", { date: c.exp })}</p>
                      </div>
                      {c.isDefault ? (
                        <Badge variant="secondary" className="rounded-full text-[10px]">{s("payment.default")}</Badge>
                      ) : (
                        <Button
                          variant="ghost" size="sm" className="h-8 rounded-lg text-xs"
                          onClick={() => makeDefault(c.id)}
                        >
                          {s("payment.makeDefault")}
                        </Button>
                      )}
                      <Button
                        variant="ghost" size="icon"
                        aria-label={s("payment.remove")}
                        className="h-8 w-8 text-destructive hover:text-destructive"
                        onClick={() => removeCard(c.id)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
                <Button
                  variant="outline" className="mt-3 h-10 w-full rounded-xl"
                  onClick={() => setCardOpen(true)}
                >
                  <Plus className="h-4 w-4" /> {s("payment.addCard")}
                </Button>
              </SectionCard>
            </TabsContent>

            {/* SUBSCRIPTION */}
            <TabsContent value="subscription" className="mt-0 space-y-6">
              <Card className="rounded-2xl border-primary/20 p-6 shadow-soft bg-gradient-to-br from-primary/10 via-background to-background">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <Badge className="rounded-full bg-primary/10 text-primary hover:bg-primary/10">{s("subscription.currentPlan")}</Badge>
                    <h3 className="mt-3 font-display text-3xl font-bold tracking-tight">{planName(currentPlan)}</h3>
                    <p className="mt-1 flex flex-wrap items-center gap-1 text-sm text-muted-foreground">
                      <Riyal /> {PLAN_PRICE[currentPlan]}
                      <span>{s("subscription.priceRenews")}</span>
                      <span className="font-semibold text-foreground">{s("subscription.renewDate")}</span>
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {currentPlan !== "Premium" && (
                      <Button
                        className="h-10 rounded-xl shadow-soft"
                        onClick={() => setSwitchTo("Premium")}
                      >
                        <ArrowUpRight className="h-4 w-4 rtl:-scale-x-100" /> {s("subscription.upgradePremium")}
                      </Button>
                    )}
                    <Button
                      variant="outline" className="h-10 rounded-xl"
                      onClick={() => (window.location.href = "/merchant/subscription")}
                    >
                      {s("subscription.manageBilling")}
                    </Button>
                  </div>
                </div>
                <div className="mt-5 grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {currentFeatures.map((f) => (
                    <div key={f} className="flex items-center gap-2 text-sm">
                      <span className="grid h-5 w-5 shrink-0 place-items-center rounded-full bg-primary/10 text-primary">
                        <Check className="h-3 w-3" />
                      </span>
                      {s(`subscription.features.${f}`)}
                    </div>
                  ))}
                </div>
              </Card>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                {planCards.map((p) => {
                  const isCurrent = p.name === currentPlan;
                  const cta = isCurrent
                    ? s("subscription.current")
                    : PLAN_PRICE[p.name] > PLAN_PRICE[currentPlan]
                    ? s("subscription.upgrade")
                    : s("subscription.downgrade");
                  return (
                    <Card key={p.name}
                      className={cn("relative rounded-2xl p-5 shadow-soft transition hover:shadow-md",
                        p.recommended ? "border-primary/40 ring-2 ring-primary/30" : "border-border/60",
                        isCurrent && "bg-primary/5")}>
                      {p.recommended && (
                        <Badge className="absolute -top-3 start-5 rounded-full bg-primary text-primary-foreground hover:bg-primary">{s("subscription.bestValue")}</Badge>
                      )}
                      <p className="font-display text-lg font-bold">{planName(p.name)}</p>
                      <div className="mt-2 flex items-baseline gap-1">
                        <span className="flex items-baseline gap-1 font-display text-3xl font-bold tracking-tight"><Riyal /> {PLAN_PRICE[p.name]}</span>
                        <span className="text-xs text-muted-foreground">{s("subscription.perMonth")}</span>
                      </div>
                      <ul className="mt-3 space-y-1.5 text-xs">
                        {p.features.map((f) => (
                          <li key={f} className="flex items-center gap-1.5">
                            <Check className="h-3 w-3 shrink-0 text-primary" /> {s(`subscription.features.${f}`)}
                          </li>
                        ))}
                      </ul>
                      <Button
                        disabled={isCurrent}
                        variant={p.recommended ? "default" : "outline"}
                        className="mt-4 h-10 w-full rounded-xl"
                        onClick={() => setSwitchTo(p.name)}
                      >
                        {cta}
                      </Button>
                    </Card>
                  );
                })}
              </div>

              <SectionCard title={s("subscription.renewalTitle")}>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                  <div className="rounded-xl bg-surface p-4">
                    <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <Calendar className="h-3.5 w-3.5" /> {s("subscription.nextRenewal")}
                    </p>
                    <p className="mt-1 font-display text-base font-semibold">{s("subscription.renewDate")}</p>
                  </div>
                  <div className="rounded-xl bg-surface p-4">
                    <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <CreditCard className="h-3.5 w-3.5" /> {s("subscription.amount")}
                    </p>
                    <p className="mt-1 flex items-center gap-1 font-display text-base font-semibold">
                      <Riyal /> {PLAN_PRICE[currentPlan]}.00
                    </p>
                  </div>
                  <div className="rounded-xl bg-surface p-4">
                    <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <Check className="h-3.5 w-3.5" /> {s("subscription.autoRenew")}
                    </p>
                    <div className="mt-1 flex items-center justify-between">
                      <p className="font-display text-base font-semibold">
                        {autoRenew ? s("subscription.enabled") : s("subscription.disabled")}
                      </p>
                      <Switch
                        checked={autoRenew}
                        onCheckedChange={(v) => {
                          setAutoRenew(v);
                          toast.success(v ? s("toast.autoRenewOn") : s("toast.autoRenewOff"));
                        }}
                      />
                    </div>
                  </div>
                </div>
                <div className="mt-4 flex items-start gap-2 rounded-xl bg-amber-500/10 p-3 text-xs text-amber-800">
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                  <p>{s("subscription.cancelNote")}</p>
                </div>
                <div className="mt-4 flex justify-end">
                  <Button
                    variant="outline"
                    className="h-10 rounded-xl text-destructive hover:text-destructive"
                    onClick={() => setCancelOpen(true)}
                  >
                    {s("subscription.cancelSubscription")}
                  </Button>
                </div>
              </SectionCard>
            </TabsContent>
          </div>
        </div>
      </Tabs>

      {/* Recovery codes dialog */}
      <Dialog open={codesOpen} onOpenChange={setCodesOpen}>
        <DialogContent className="max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle>{s("security.codesDialog.title")}</DialogTitle>
            <DialogDescription>{s("security.codesDialog.desc")}</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-2 rounded-xl bg-surface p-4 font-mono text-sm" dir="ltr">
            {recoveryCodes.map((c) => (
              <div key={c} className="rounded-md bg-background px-3 py-2 text-center">{c}</div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={copyCodes}>
              <Copy className="h-4 w-4" /> {s("security.codesDialog.copyAll")}
            </Button>
            <Button className="rounded-xl" onClick={() => setCodesOpen(false)}>{s("security.codesDialog.done")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Revoke session confirm */}
      <AlertDialog open={!!revokeId} onOpenChange={(v) => !v && setRevokeId(null)}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>{s("security.revokeDialog.title")}</AlertDialogTitle>
            <AlertDialogDescription>{s("security.revokeDialog.desc")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">{s("security.revokeDialog.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              className="rounded-xl bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                setSessions((x) => x.filter((y) => y.id !== revokeId));
                toast.success(s("toast.sessionRevoked"));
                setRevokeId(null);
              }}
            >
              {s("security.revokeDialog.confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Add wallet dialog */}
      <Dialog open={walletOpen} onOpenChange={setWalletOpen}>
        <DialogContent className="max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle>{s("payment.walletDialog.title")}</DialogTitle>
            <DialogDescription>{s("payment.walletDialog.desc")}</DialogDescription>
          </DialogHeader>
          <div className="grid gap-3">
            <div>
              <Label className="text-xs text-muted-foreground">{s("payment.walletDialog.provider")}</Label>
              <div className="mt-1.5 grid grid-cols-3 gap-2">
                {(["PayPal", "Apple Pay", "Google Pay"] as const).map((k) => (
                  <button
                    key={k}
                    onClick={() => setWalletForm((f) => ({ ...f, kind: k }))}
                    className={cn(
                      "rounded-xl border p-2.5 text-xs font-semibold transition",
                      walletForm.kind === k
                        ? "border-primary bg-primary/10 text-primary"
                        : "border-border/60 hover:border-primary/40",
                    )}
                  >
                    {k}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label htmlFor="w-label" className="text-xs text-muted-foreground">{s("payment.walletDialog.label")}</Label>
              <Input
                id="w-label" placeholder="you@example.com" dir="ltr"
                value={walletForm.label}
                onChange={(e) => setWalletForm((f) => ({ ...f, label: e.target.value }))}
                className="mt-1.5 h-11 rounded-xl"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => setWalletOpen(false)}>{s("payment.walletDialog.cancel")}</Button>
            <Button className="rounded-xl" onClick={addWallet}>{s("payment.walletDialog.submit")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add card dialog */}
      <Dialog open={cardOpen} onOpenChange={setCardOpen}>
        <DialogContent className="max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle>{s("payment.cardDialog.title")}</DialogTitle>
            <DialogDescription>{s("payment.cardDialog.desc")}</DialogDescription>
          </DialogHeader>
          <div className="grid gap-3">
            <div>
              <Label htmlFor="c-num" className="text-xs text-muted-foreground">{s("payment.cardDialog.number")}</Label>
              <Input
                id="c-num" inputMode="numeric" placeholder="1234 5678 9012 3456" dir="ltr"
                value={cardForm.number}
                onChange={(e) => setCardForm({ ...cardForm, number: e.target.value })}
                className="mt-1.5 h-11 rounded-xl font-mono"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="c-exp" className="text-xs text-muted-foreground">{s("payment.cardDialog.exp")}</Label>
                <Input
                  id="c-exp" placeholder="09/28" maxLength={5} dir="ltr"
                  value={cardForm.exp}
                  onChange={(e) => setCardForm({ ...cardForm, exp: e.target.value })}
                  className="mt-1.5 h-11 rounded-xl"
                />
              </div>
              <div>
                <Label htmlFor="c-cvc" className="text-xs text-muted-foreground">{s("payment.cardDialog.cvc")}</Label>
                <Input
                  id="c-cvc" placeholder="123" maxLength={4} inputMode="numeric" dir="ltr"
                  value={cardForm.cvc}
                  onChange={(e) => setCardForm({ ...cardForm, cvc: e.target.value })}
                  className="mt-1.5 h-11 rounded-xl"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => setCardOpen(false)}>{s("payment.cardDialog.cancel")}</Button>
            <Button className="rounded-xl" onClick={addCard}>{s("payment.cardDialog.submit")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Plan switch confirm */}
      <AlertDialog open={!!switchTo} onOpenChange={(v) => !v && setSwitchTo(null)}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {switchTo && PLAN_PRICE[switchTo] > PLAN_PRICE[currentPlan]
                ? s("subscription.switchDialog.titleUpgrade", { plan: switchTo ? planName(switchTo) : "" })
                : s("subscription.switchDialog.titleDowngrade", { plan: switchTo ? planName(switchTo) : "" })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {s("subscription.switchDialog.desc", { price: switchTo ? PLAN_PRICE[switchTo] : 0 })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">{s("subscription.switchDialog.cancel")}</AlertDialogCancel>
            <AlertDialogAction className="rounded-xl" onClick={confirmSwitch}>
              {s("subscription.switchDialog.confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Cancel subscription */}
      <AlertDialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>{s("subscription.cancelDialog.title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {s("subscription.cancelDialog.desc", { plan: planName(currentPlan) })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">{s("subscription.cancelDialog.keep")}</AlertDialogCancel>
            <AlertDialogAction
              className="rounded-xl bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                setAutoRenew(false);
                setCancelOpen(false);
                toast.success(s("toast.cancelled"), {
                  description: s("toast.cancelledDesc"),
                });
              }}
            >
              {s("subscription.cancelDialog.confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </MerchantShell>
  );
}
